'use client'

import {
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react'
import { cn } from '@/lib/utils'

const pillClassName =
  'group relative inline-flex shrink-0 items-center justify-center overflow-visible text-[15px] font-semibold text-black dark:text-white transition-colors px-7 py-3 rounded-full hover:bg-black/5 dark:hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2'

function useAnimatedPillMetrics<T extends HTMLElement>(
  elementRef: React.RefObject<T | null>,
  borderDrawDelayMs = 0
) {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = elementRef.current
    if (!el) return

    const update = () => {
      const rect = el.getBoundingClientRect()
      const width = Math.max(1, rect.width)
      const height = Math.max(1, rect.height)
      setSize({ width, height })
    }

    update()
    const ro = new ResizeObserver(() => update())
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const el = elementRef.current
    if (!el) return

    let timeoutId: number | null = null

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting) {
          observer.disconnect()
          if (borderDrawDelayMs > 0) {
            timeoutId = window.setTimeout(() => {
              setInView(true)
              timeoutId = null
            }, borderDrawDelayMs)
          } else {
            setInView(true)
          }
        }
      },
      { threshold: 0.2 }
    )

    observer.observe(el)
    return () => {
      observer.disconnect()
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [borderDrawDelayMs])

  const vbWidth = size?.width ?? 140
  const vbHeight = size?.height ?? 44

  return { vbWidth, vbHeight, inView }
}

function PillBorderSvg({
  vbWidth,
  vbHeight,
  animate,
}: {
  vbWidth: number
  vbHeight: number
  animate: boolean
}) {
  /** Inset so 1px stroke stays inside the viewBox (avoids clipped corners on tight pills). */
  const inset = 1.5
  const rw = Math.max(0, vbWidth - inset * 2)
  const rh = Math.max(0, vbHeight - inset * 2)
  const rr = rh > 0 ? rh / 2 : 0

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
      viewBox={`0 0 ${vbWidth} ${vbHeight}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <rect
        x={inset}
        y={inset}
        width={rw}
        height={rh}
        rx={rr}
        ry={rr}
        fill="none"
        className="opacity-0"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <rect
        x={inset}
        y={inset}
        width={rw}
        height={rh}
        rx={rr}
        ry={rr}
        fill="none"
        className={`border-draw-path text-black/40 dark:text-white/40 ${
          animate ? 'animate-border-draw' : ''
        }`}
        stroke="currentColor"
        strokeWidth="1"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        pathLength={1}
      />
    </svg>
  )
}

type AnchorOmit = Omit<ComponentPropsWithoutRef<'a'>, 'className' | 'children'>
type ButtonOmit = Omit<ComponentPropsWithoutRef<'button'>, 'className' | 'children' | 'type'>

export function AnimatedPillLink({
  children,
  className,
  borderDrawDelayMs = 0,
  ...props
}: AnchorOmit & { children: ReactNode; className?: string; borderDrawDelayMs?: number }) {
  const ref = useRef<HTMLAnchorElement>(null)
  const { vbWidth, vbHeight, inView } = useAnimatedPillMetrics(ref, borderDrawDelayMs)

  return (
    <a
      ref={ref}
      className={cn(pillClassName, className)}
      {...props}
    >
      <PillBorderSvg vbWidth={vbWidth} vbHeight={vbHeight} animate={inView} />
      {children}
    </a>
  )
}

export function AnimatedPillButton({
  children,
  className,
  borderDrawDelayMs = 0,
  ...props
}: ButtonOmit & { children: ReactNode; className?: string; borderDrawDelayMs?: number }) {
  const ref = useRef<HTMLButtonElement>(null)
  const { vbWidth, vbHeight, inView } = useAnimatedPillMetrics(ref, borderDrawDelayMs)

  return (
    <button
      ref={ref}
      {...props}
      type="button"
      className={cn(pillClassName, className)}
    >
      <PillBorderSvg vbWidth={vbWidth} vbHeight={vbHeight} animate={inView} />
      {children}
    </button>
  )
}
